<template>
  <v-row>
    <v-col cols="12">
      <v-breadcrumbs :items="crumbs" class="pa-0 mb-2"></v-breadcrumbs>

      <v-card :loading="loading">
        <v-card-title class="py-3">
          <v-btn icon :to="'/report/budget-monitoring'" class="mr-2"
            ><v-icon>mdi-arrow-left</v-icon></v-btn
          >
          <div>
            <div class="headline">{{ summary.jobName || "Job #" + jobId }}</div>
            <div class="caption grey--text text--darken-1">
              {{ summary.customer || "—" }} · dihitung {{ formatDateTime(summary.computedAt) }}
              <span v-for="s in shares" :key="s.crmPoId">
                ·
                <router-link :to="'/report/budget-monitoring/po/' + s.crmPoId">{{
                  s.poNumber || "#" + s.crmPoId
                }}</router-link>
              </span>
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-chip large outlined :color="bandColor(summary.bandInfo)" class="mr-3">
            {{ bandLabel(summary.bandInfo) }} · {{ formatPct(summary.ratioPercent) }} (info)
          </v-chip>
          <v-btn outlined color="primary" :loading="recomputing" @click="recompute">
            <v-icon left>mdi-refresh</v-icon>Hitung ulang
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text>
          <v-alert type="info" dense text class="mb-3">
            Band per job bersifat informasi; email dikirim per PO CRM. Budget job = Σ share budget
            PO.
          </v-alert>
          <v-row dense>
            <v-col cols="12" sm="6" md="3">
              <v-card outlined class="pa-3 fill-height">
                <div class="caption grey--text text-uppercase">Budget (Σ share)</div>
                <div class="title">Rp {{ formatIdr(summary.budgetIdr) }}</div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card outlined class="pa-3 fill-height">
                <div class="caption grey--text text-uppercase">Belanja PO</div>
                <div class="title">
                  Rp {{ formatIdr(summary.spendIdr) }}
                  <v-chip
                    x-small
                    color="blue lighten-5"
                    class="blue--text text--darken-2 ml-1"
                    @click="openBreakdown('SPEND')"
                    >rincian</v-chip
                  >
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card outlined class="pa-3 fill-height">
                <div class="caption grey--text text-uppercase">Stok</div>
                <div class="title">
                  Rp {{ formatIdr(summary.stockIdr) }}
                  <v-chip
                    x-small
                    color="indigo lighten-5"
                    class="indigo--text ml-1"
                    @click="openBreakdown('STOCK')"
                    >rincian</v-chip
                  >
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card outlined class="pa-3 fill-height">
                <div class="caption grey--text text-uppercase">Pemakaian · Committed</div>
                <div class="title">Rp {{ formatIdr(summary.usageIdr) }}</div>
                <div class="caption grey--text">
                  committed Rp {{ formatIdr(summary.committedIdr) }}
                  <v-chip x-small class="ml-1" @click="openBreakdown('COMMITTED')">rincian</v-chip>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-card outlined class="mt-4">
            <v-card-title class="subtitle-1 py-2">Per PO CRM</v-card-title>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>PO CRM</th>
                  <th class="text-right">Share budget (IDR)</th>
                  <th class="text-right">Belanja ke PO</th>
                  <th class="text-right">Stok ke PO</th>
                  <th class="text-right">Committed</th>
                  <th class="text-right">Pemakaian ke PO</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in perPo" :key="row.crmPoId">
                  <td>
                    <router-link
                      v-if="row.crmPoId"
                      :to="'/report/budget-monitoring/po/' + row.crmPoId"
                      >{{ row.poNumber || "#" + row.crmPoId }}</router-link
                    >
                    <span v-else class="grey--text">tanpa PO (NONE)</span>
                  </td>
                  <td class="text-right">{{ formatIdr(row.budgetIdr) }}</td>
                  <td class="text-right">
                    {{ formatIdr(row.spend) }}
                    <v-chip
                      x-small
                      color="blue lighten-5"
                      class="blue--text text--darken-2 ml-1"
                      v-if="row.crmPoId"
                      @click="openBreakdown('SPEND', row)"
                      >rincian</v-chip
                    >
                  </td>
                  <td class="text-right">
                    {{ formatIdr(row.stock) }}
                    <v-chip
                      x-small
                      color="indigo lighten-5"
                      class="indigo--text ml-1"
                      v-if="row.crmPoId"
                      @click="openBreakdown('STOCK', row)"
                      >rincian</v-chip
                    >
                  </td>
                  <td class="text-right">{{ formatIdr(row.committed) }}</td>
                  <td class="text-right font-weight-bold">
                    {{ formatIdr(row.spend + row.stock) }}
                  </td>
                </tr>
                <tr v-if="!perPo.length">
                  <td colspan="6" class="grey--text text-center">
                    Job ini belum terhubung ke PO CRM
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-card-text>
      </v-card>
    </v-col>

    <budget-spend-breakdown v-model="showBreakdown" :context="breakdown"></budget-spend-breakdown>
  </v-row>
</template>

<script>
import BudgetSpendBreakdown from "@/components/budget/BudgetSpendBreakdown.vue";
import { bandColor, bandLabel, formatIdr, formatPct } from "@/utils/budgetMonitor";

export default {
  name: "budget-monitoring-job-detail",
  components: { BudgetSpendBreakdown },
  data: () => ({
    loading: false,
    recomputing: false,
    detail: null,
    groups: [],
    showBreakdown: false,
    breakdown: { bucket: "SPEND", expectedAmount: 0 },
  }),
  computed: {
    jobId() {
      return Number(this.$route.params.jobId);
    },
    summary() {
      return (this.detail && this.detail.summary) || {};
    },
    shares() {
      return (this.detail && this.detail.budgetShares) || [];
    },
    crumbs() {
      return [
        { text: "Report", to: "/report", exact: true },
        { text: "Budget Monitoring", to: "/report/budget-monitoring", exact: true },
        { text: this.summary.jobName || "Job #" + this.jobId, disabled: true },
      ];
    },
    /** usage of this job split by PO CRM: shares from the snapshot + Σ lines per crmPoId */
    perPo() {
      const map = {};
      this.shares.forEach(s => {
        map[s.crmPoId] = {
          crmPoId: s.crmPoId,
          poNumber: s.poNumber,
          budgetIdr: Number(s.budgetIdr || 0),
          spend: 0,
          stock: 0,
          committed: 0,
        };
      });
      this.groups.forEach(g => {
        (g.lines || []).forEach(l => {
          const key = l.crmPoId || 0;
          if (!map[key]) {
            map[key] = {
              crmPoId: l.crmPoId,
              poNumber: null,
              budgetIdr: 0,
              spend: 0,
              stock: 0,
              committed: 0,
            };
          }
          const amt = Number(l.amountIdr || 0);
          if (l.bucket === "SPEND") map[key].spend += amt;
          else if (l.bucket === "STOCK") map[key].stock += amt;
          else if (l.bucket === "COMMITTED") map[key].committed += amt;
        });
      });
      return Object.values(map);
    },
  },
  watch: {
    "$route.params.jobId"() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    formatIdr,
    formatPct,
    bandColor,
    bandLabel,
    formatDateTime(d) {
      if (!d) return "—";
      const s = typeof d === "string" ? d : new Date(d).toISOString();
      return s.substring(0, 16).replace("T", " ");
    },
    async load() {
      this.loading = true;
      try {
        this.detail = await this.$store.dispatch("budgetMonitor/fetchJobDetail", this.jobId);
        this.groups = await this.$store.dispatch("budgetMonitor/fetchJobLines", {
          jobId: this.jobId,
        });
      } catch (e) {
        const status = e && e.response && e.response.status;
        this.$store.dispatch("toast", {
          type: "error",
          message: status === 404 ? "Job belum pernah dihitung" : "Gagal memuat detail job",
        });
      } finally {
        this.loading = false;
      }
    },
    openBreakdown(bucket, poRow) {
      const expected = poRow
        ? bucket === "STOCK"
          ? poRow.stock
          : bucket === "COMMITTED"
          ? poRow.committed
          : poRow.spend
        : bucket === "STOCK"
        ? this.summary.stockIdr
        : bucket === "COMMITTED"
        ? this.summary.committedIdr
        : this.summary.spendIdr;
      this.breakdown = {
        crmPoId: poRow ? poRow.crmPoId : undefined,
        jobId: this.jobId,
        jobName: this.summary.jobName || "Job " + this.jobId,
        bucket,
        expectedAmount: expected,
        poNumber: poRow ? poRow.poNumber : undefined,
      };
      this.showBreakdown = true;
    },
    async recompute() {
      this.recomputing = true;
      try {
        const res = await this.$store.dispatch("budgetMonitor/recomputeJob", this.jobId);
        this.$store.dispatch("toast", {
          type: res && res.status === "DONE" ? "success" : "warning",
          message: `Perhitungan ${res && res.status}`,
        });
        await this.load();
      } catch (e) {
        const status = e && e.response && e.response.status;
        this.$store.dispatch("toast", {
          type: status === 409 ? "warning" : "error",
          message: status === 409 ? "Perhitungan lain sedang berjalan" : "Gagal menghitung ulang",
        });
      } finally {
        this.recomputing = false;
      }
    },
  },
};
</script>
